import { useState } from "react";
import {
  MapPin,
  Clock,
  CheckCircle,
  Undo2,
  Hourglass,
  Loader2,
} from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { CustomWageNotice } from "./CustomWageNotice";
import type { SubmittedShift } from "../hooks/useSubmittedTimeEntries";
import {
  formatWageType,
  formatDuration,
  calculateDurationHours,
  formatDateInTimezone,
  formatTimeInTimezone,
} from "../utils/time-entry-schema";

interface SubmittedTimeEntryCardProps {
  shift: SubmittedShift;
  onWithdraw?: (shiftId: string) => void;
  isWithdrawing?: boolean;
}

export function SubmittedTimeEntryCard({
  shift,
  onWithdraw,
  isWithdrawing,
}: SubmittedTimeEntryCardProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const isPending = shift.status === "pending_org_review";
  const isApproved = shift.status === "approved";

  const scheduledStart = new Date(shift.scheduledStartsAt);
  const submittedStart = shift.timeEntries.reduce<Date | null>(
    (earliest, entry) => {
      const entryStart = new Date(entry.startsAt);
      return !earliest || entryStart < earliest ? entryStart : earliest;
    },
    null,
  );
  // A submitted timesheet can be moved to a different day than its original
  // shift. Display the actual work date, falling back only for empty entries.
  const displayStart = submittedStart ?? scheduledStart;

  // Calculate total hours from all time entries
  const totalHours = shift.timeEntries.reduce((sum, entry) => {
    return (
      sum +
      calculateDurationHours(
        new Date(entry.startsAt).toISOString(),
        new Date(entry.endsAt).toISOString(),
      )
    );
  }, 0);

  return (
    <Card
      className={cn(
        "overflow-hidden",
        isPending && "border-amber-200 bg-amber-50/30",
        isApproved && "border-green-200 bg-green-50/30",
      )}
    >
      <CardHeader className="pb-3 bg-gradient-to-r from-slate-50 to-slate-100 border-b">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Date as primary */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg font-bold text-slate-900">
                {formatDateInTimezone(displayStart, shift.timezoneId)}
              </span>
              {isPending && (
                <Badge
                  variant="outline"
                  className="text-xs bg-amber-100 text-amber-700 border-amber-200"
                >
                  <Hourglass className="h-3 w-3 mr-1" />
                  Pending Review
                </Badge>
              )}
              {isApproved && (
                <Badge
                  variant="outline"
                  className="text-xs bg-green-100 text-green-700 border-green-200"
                >
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Approved
                </Badge>
              )}
            </div>

            {/* Facility info */}
            <div className="flex items-start gap-1.5 text-sm text-slate-600">
              <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5 shrink-0" />
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1.5">
                <span className="truncate">{shift.facilityName}</span>
                {shift.facilityCity && shift.facilityState && (
                  <span className="text-slate-400">
                    <span className="hidden sm:inline">• </span>
                    {shift.facilityCity}, {shift.facilityState}
                  </span>
                )}
              </div>
            </div>

            {/* Job offer title if present */}
            {shift.jobOfferTitle && (
              <div className="text-sm text-slate-500 mt-0.5 truncate">
                {shift.jobOfferTitle}
              </div>
            )}
          </div>

          {/* Withdraw button for pending entries */}
          {isPending && onWithdraw && (
            <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isWithdrawing}
                  className="shrink-0 text-amber-700 border-amber-300 hover:bg-amber-50 hover:text-amber-800"
                >
                  {isWithdrawing ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Undo2 className="h-4 w-4 mr-1" />
                  )}
                  Withdraw
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Withdraw Time Entry?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will remove your submitted time entry for{" "}
                    <span className="font-medium text-slate-700">
                      {formatDateInTimezone(displayStart, shift.timezoneId)}
                    </span>{" "}
                    at {shift.facilityName}. You'll need to resubmit your hours
                    for this shift.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      onWithdraw(shift.shiftId);
                      setDialogOpen(false);
                    }}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    Withdraw Entry
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </CardHeader>

      <CardContent className="pt-4">
        {/* Time entries summary */}
        <div className="space-y-2">
          {shift.timeEntries.map((entry, index) => {
            const entryStart = new Date(entry.startsAt);
            const entryEnd = new Date(entry.endsAt);
            const hours = calculateDurationHours(
              entryStart.toISOString(),
              entryEnd.toISOString(),
            );

            return (
              <div
                key={entry.id}
                className={cn(
                  "flex items-center justify-between py-2 px-3 rounded-md",
                  entry.isOvertime
                    ? "bg-blue-50 border border-blue-100"
                    : "bg-slate-50",
                )}
              >
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <div>
                    <div className="text-sm font-medium text-slate-700">
                      {formatTimeInTimezone(entryStart, shift.timezoneId)} -{" "}
                      {formatTimeInTimezone(entryEnd, shift.timezoneId)}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-2">
                      <span>{formatWageType(entry.baseWageType)}</span>
                      {entry.isOvertime && (
                        <Badge
                          variant="outline"
                          className="text-xs bg-blue-100 text-blue-700 border-blue-200 py-0"
                        >
                          OT
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <div className="text-sm font-semibold text-slate-700">
                  {formatDuration(hours)}
                </div>
              </div>
            );
          })}
        </div>

        {shift.customWage && (
          <div className="mt-3">
            <CustomWageNotice customWage={shift.customWage} />
          </div>
        )}

        {/* Total hours */}
        <div className="mt-3 pt-3 border-t flex items-center justify-between">
          <span className="text-sm text-slate-600">Total Hours</span>
          <span className="font-semibold text-slate-900">
            {formatDuration(totalHours)}
          </span>
        </div>

        {/* Provider note if any */}
        {shift.timeEntries.some((e) => e.providerNote) && (
          <div className="mt-3 pt-3 border-t">
            <div className="text-xs text-slate-500 mb-1">Notes</div>
            {shift.timeEntries
              .filter((e) => e.providerNote)
              .map((entry) => (
                <div key={entry.id} className="text-sm text-slate-600">
                  {entry.providerNote}
                </div>
              ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
